import { useState, useEffect, useMemo, useRef } from "react";
import SendingFrom from './Email/SendingFrom';
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast, showSuccessToast } from '../utils/toast';        // Block 233
import { invoiceDisplayStatus, isPaidStatus, keptTotal } from "../utils/invoiceOverdue";   // decision 44; Block H-5: one status, refunds
import { useDeepLinkTarget, useDeepLinkItem, clearPendingDeepLink } from "../utils/deepLink"; // Block D20/D23: notification item deep-link
import { useTranslation } from "react-i18next";
import { useContacts } from "../contexts/ContactsContext";
import { useInvoices } from "../contexts/InvoicesContext";
import { useExpenses } from "../contexts/ExpensesContext";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { api } from "../api/client";
import { getProjects, createProject } from "../services/projectService";
import { formatMoney } from "../utils/currency";   // Block K: the ONE money formatter (no local symbol map)
import { expenseRebillLine } from "../lib/expenseRebill";   // 2026-10-20: a re-billed expense, net + VAT once
// Item 34: the one rounding rule, the rate as a choice, the business's VAT settings, the one renderer.
import { invoiceAmounts } from "../lib/invoiceAmounts";
import { RATE_CHOICES, RATE_TYPES, isRateType, rateTypeForPercent } from "../lib/vatRates";
import { useBusinessVat } from "../lib/useBusinessVat";
import InvoiceBody from "./Invoicing/InvoiceBody";
import { HeaderButton,
  Card, StatTile, TabGroup, SearchInput, Select, Button, OverflowMenu, Modal, ConfirmDialog, Banner, EmptyState,
  ErrorState, Skeleton, StatusPill, Field, Input, Textarea, Checkbox, Chip, useDelayedDelete, useBreakpoint, Reveal,
} from "../ui";
import PhoneListFilters from "./common/PhoneListFilters";   // fresh-eyes item 21
import SectionHead from "./Invoicing/SectionHead";
import XeroSyncPanel from "./Invoicing/XeroSyncPanel";   // M2: the Xero sync status on the invoice
import { featureAllowed } from "../utils/planRule";   // UI-24 item 19: the Xero push is Pro
import { canDeleteInvoice } from "../utils/invoiceDeletable";   // UI-24 item 19, decision 2: sent is cancelled, never deleted
import "./Invoicing/invoicing.css";
import { downloadInvoicePdf } from '../utils/invoicePdf';   // one download, shared with the chat's PDF card

// Block K (UI-22 Invoicing, 2026-10-11): the Invoices section on the v2 kit, modelled on Proposals.
// The page header and the section tabs are InvoicingLayout's; this is one section of that page.

// ─── Constants ────────────────────────────────────────────────────────────────
// The names for the currency picker. The SYMBOLS come from utils/currency (one map for the whole
// app): this file had its own, and wrote AED as "د.إ" and CHF without its space.
const CURRENCIES = [
  { code: "GBP", name: "British Pound" },
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "SEK", name: "Swedish Krona" },
  { code: "NOK", name: "Norwegian Krone" },
  { code: "DKK", name: "Danish Krone" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "AED", name: "UAE Dirham" },
  { code: "ZAR", name: "South African Rand" },
];

const RECUR_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

// One word per displayed status (invoiceDisplayStatus), its pill tone and its card colour.
const STATUS = {
  draft:              { label: "Draft",              tone: "neutral", color: "pastel-neutral" },
  pending:            { label: "Due",                tone: "pending", color: "pastel-blue" },   // fresh-eyes 24: was "Pending"
  overdue:            { label: "Overdue",            tone: "warning", color: "pastel-coral" },
  paid:               { label: "Paid",               tone: "success", color: "pastel-green" },
  cancelled:          { label: "Cancelled",          tone: "neutral", color: "pastel-neutral" },
  // Block H-5 (Carla's decision 4)
  partially_refunded: { label: "Partially refunded", tone: "success", color: "pastel-lilac" },
  refunded:           { label: "Refunded",           tone: "neutral", color: "pastel-periwinkle" },
};

const TABS = ["All", "Due", "Paid", "Overdue", "Refunded", "Recurring", "Drafts"];
const TAB_COLORS = { All: "pastel-lilac", Due: "pastel-blue", Paid: "pastel-green", Overdue: "pastel-coral", Refunded: "pastel-periwinkle", Recurring: "pastel-mint", Drafts: "pastel-neutral" };
// Block H-5: each status tab shows exactly the invoices whose ROW says one of its words. Paid keeps a
// partly refunded invoice (money is still in); Refunded is the ones given back in full.
const TAB_STATUS = { Due: ["pending"], Paid: ["paid", "partially_refunded"], Overdue: ["overdue"], Refunded: ["refunded"], Drafts: ["draft"] };
function inTab(inv, tab) {
  if (tab === "All") return true;
  if (tab === "Recurring") return !!inv.recurring && inv.status !== "draft";
  return (TAB_STATUS[tab] || []).includes(invoiceDisplayStatus(inv));
}
function countForTab(invoices, tab) {
  return invoices.filter(i => inTab(i, tab)).length;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Item 34: the screen no longer invents invoice numbers (getNextNumber read the invoices it had
// loaded, so two people, or one stale list, could make the same number). The server's counter
// numbers every invoice when it is saved, and a sent invoice's number cannot change.

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function addFrequency(dateStr, freq) {
  const d = new Date(dateStr);
  if (freq === "weekly") d.setDate(d.getDate() + 7);
  if (freq === "monthly") d.setMonth(d.getMonth() + 1);
  if (freq === "quarterly") d.setMonth(d.getMonth() + 3);
  if (freq === "annually") d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

// Item 34: the one rule, per line (lib/invoiceAmounts, the twin of the server's), never a local sum.
function calcTotals(lineItems, currency = "GBP") {
  const { subtotal, tax, total } = invoiceAmounts({ lineItems, currency });
  return { subtotal, tax, total };
}

// Every money figure on this page: the shared formatter, in the invoice's own currency.
function fmtMoney(amount, code) {
  return formatMoney(Number(amount) || 0, code || "GBP");
}

// Decision 72: never add currencies together. "£3,465.00 plus €1,200.00", the business's own
// currency first (as Financial overview does). Decision 71 is the caller's: which invoices count.
function moneyByCurrency(list, amountOf, base = "GBP") {
  const by = {};
  for (const inv of list) {
    const c = inv.currency || "GBP";
    by[c] = (by[c] || 0) + (Number(amountOf(inv)) || 0);
  }
  const codes = Object.keys(by).filter(c => Math.abs(by[c]) > 0.0049)
    .sort((a, b) => (a === base ? -1 : b === base ? 1 : a.localeCompare(b)));
  if (!codes.length) return fmtMoney(0, base);
  return codes.map(c => fmtMoney(by[c], c)).join(" plus ");
}

function fmtDate(str) {
  if (!str) return "None";
  const d = new Date(str);
  if (isNaN(d.getTime())) return String(str);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
const shortDate = (str) => {
  const d = new Date(str);
  return isNaN(d.getTime()) ? String(str || "") : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// Item 34: a new line takes the business's default VAT rate when it is VAT registered, and no VAT
// when it is not (the server refuses any other).
function lineRateFields(vat) {
  if (!vat || !vat.registered) return { tax: 0 };
  const type = isRateType(vat.defaultRate) ? vat.defaultRate : "standard";
  return { rateType: type, tax: RATE_TYPES[type].percent };
}
function emptyLine(vat) {
  return { id: Date.now() + Math.random(), description: "", qty: 1, unitPrice: 0, ...lineRateFields(vat) };
}
// The type a line's select shows: its own, else the one its percent can only mean, else none chosen.
const shownRateType = (r) => (isRateType(r.rateType) ? r.rateType : (rateTypeForPercent(r.tax) || ""));

// The API's invoice in this page's shape (as InvoicesContext normalises a load).
function fromServer(inv) {
  return {
    ...inv,
    id: Number(inv.id),
    project_id: inv.projectId ?? inv.project_id ?? null,
    linkedTaskId: inv.linkedTaskId != null ? Number(inv.linkedTaskId) : null,
    lineItems: inv.lineItems || [],
    reminders: inv.reminders || [],
    recurring: inv.recurring || null,
  };
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const REMINDER_PRESETS = [
  { daysOffset: -3, label: "3 days before due" },
  { daysOffset: -1, label: "1 day before due" },
  { daysOffset: 0, label: "On due date" },
  { daysOffset: 3, label: "3 days after due" },
  { daysOffset: 7, label: "7 days after due" },
  { daysOffset: 14, label: "14 days after due" },
];

// Write a payment-chase task via the API and return a promise resolving to its id
async function createChaseTask(inv) {
  const { total } = calcTotals(inv.lineItems, inv.currency);
  const start = new Date(`${inv.dueDate}T09:00:00`);
  const end = new Date(start.getTime() + 3600000);
  const task = {
    title: `Chase payment, ${inv.number} (${inv.contactName})`,
    datetime: start.toISOString(),
    deadline: end.toISOString(),
    taskType: "Invoices",
    // No priority: nobody chose one, so it takes her usual one (Aidey Backend/lib/learntDefaults.js), else Medium.
    details: `Invoice ${inv.number} for ${fmtMoney(total, inv.currency)} is due. Check whether payment has been received from ${inv.contactName} and mark as paid.`,
    completed: false,
    assignedTeam: [],
    busy: false,
    invoiceId: inv.id,
  };
  const res = await api('/api/tasks', { method: 'POST', body: JSON.stringify(task) });
  const data = await res.json();
  return data.task?.id ?? null;
}

// Mark the linked chase task as completed
function completeLinkedTask(taskId) {
  if (!taskId) return;
  api(`/api/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify({ completed: true }) })
    .catch(e => console.warn("Could not complete chase task:", e));
}

// Calculate the ISO date for a reminder given invoice dueDate + offset
function reminderDate(dueDate, daysOffset) {
  return addDays(dueDate, daysOffset);
}

const today = new Date().toISOString().slice(0, 10);

// ─── PDF export ───────────────────────────────────────────────────────────────
// ── ONE DOWNLOAD, USED BY BOTH BUTTONS ───────────────────────────────────────────────────────
//
// There are TWO "Download PDF" controls: one in the invoice detail and one on every invoice in
// the list (its ⋯ menu). On 2026-10-06 I replaced the detail one and MISSED the list one, which still
// called printInvoice — so a button whose tooltip read "Download PDF" opened a print dialogue showing
// the OLD Aidey-branded HTML with no supplier block. A verification pass found it.
//
// That is the second-path rule failing inside the fix written to satisfy it, so the download is now
// one function and both controls call it. There is nothing left to miss.
//
// api() returns the Response, so the bytes are READ rather than assumed. A route that 404s or 500s
// returns JSON, and turning that into a blob produces a file named like an invoice containing an
// error message — a download that looks like it worked. Status and content type are both checked.
//
// Returns null on success, or a message to show. It does not toast: the detail shows the message
// at its button, and the list has nowhere to put one, so the caller decides.
// The function itself now lives in utils/invoicePdf.js, so the chat's PDF card is the same download.

// ── PRINT IS THE PDF (item 34) ────────────────────────────────────────────────────────────────
// Print built its own HTML in a new window, headed "Aidey", with its own sums and a "Tax %" column:
// a second invoice renderer. It now opens the ONE document, the PDF the client gets
// (GET /api/invoices/:id/pdf?inline=1), in the browser's viewer, which prints it. The window is
// opened in the click itself, so a pop-up blocker allows it, and is pointed at the PDF once loaded.
async function printInvoicePdf(invoice) {
  const win = window.open("", "_blank");
  if (!win) { showErrorToast("Allow pop-ups to print invoices."); return; }
  try {
    const res = await api(`/api/invoices/${invoice.id}/pdf?inline=1`);
    const blob = await res.blob();
    if (blob.type && !blob.type.includes("pdf")) throw new Error("That did not open for printing. Please try again.");
    win.location.href = URL.createObjectURL(blob);
  } catch (err) {
    win.close();
    showErrorToast(messageFromError(err, "That did not open for printing. Please try again."));
  }
}

// ─── Line items editor ────────────────────────────────────────────────────────
// A grid on desktop; on a phone each line is its own small stack with its labels (as Proposals).
function LineItemsEditor({ items, onChange, currency, vat }) {
  const registered = !!(vat && vat.registered);
  function update(id, field, val) {
    onChange(items.map(r => {
      if (r.id !== id) return r;
      // Item 34: the rate is a CHOICE of type; its percent follows from the type, never typed.
      if (field === "rateType") return isRateType(val) ? { ...r, rateType: val, tax: RATE_TYPES[val].percent } : { ...r, rateType: undefined };
      return { ...r, [field]: field === "description" ? val : parseFloat(val) || 0 };
    }));
  }
  function remove(id) { onChange(items.filter(r => r.id !== id)); }
  function add() { onChange([...items, emptyLine(vat)]); }

  const { subtotal, tax, total } = calcTotals(items, currency);
  const lines = invoiceAmounts({ lineItems: items, currency }).lines;

  return (
    <div className="invc-lines">
      <div className="invc-lines__table">
        <div className={`invc-lines__head${registered ? "" : " invc-lines__head--novat"}`} aria-hidden="true">
          <span>Description</span><span>Qty</span><span>{registered ? "Unit price (excl. VAT)" : "Unit price"}</span>{registered && <span>VAT rate</span>}<span>{registered ? "Amount (excl. VAT)" : "Amount"}</span><span />
        </div>
        {items.map((r, idx) => {
          const lineTotal = lines[idx] ? lines[idx].net : 0;
          return (
            <div key={r.id} className={`invc-lines__row${registered ? "" : " invc-lines__row--novat"}`}>
              <label className="invc-lines__cell invc-lines__cell--name">
                <span className="invc-lines__lab">Description</span>
                <Input value={r.description} onChange={e => update(r.id, "description", e.target.value)} placeholder="Item description" aria-label={`Line ${idx + 1} description`} />
              </label>
              <label className="invc-lines__cell invc-lines__cell--qty">
                <span className="invc-lines__lab">Qty</span>
                <Input type="number" min="1" inputMode="decimal" value={r.qty} onChange={e => update(r.id, "qty", e.target.value)} aria-label={`Line ${idx + 1} quantity`} />
              </label>
              <label className="invc-lines__cell invc-lines__cell--price">
                <span className="invc-lines__lab">Unit price</span>
                <Input type="number" min="0" step="0.01" inputMode="decimal" value={r.unitPrice} onChange={e => update(r.id, "unitPrice", e.target.value)} aria-label={`Line ${idx + 1} unit price`} />
              </label>
              {registered && (
                <label className="invc-lines__cell invc-lines__cell--tax">
                  <span className="invc-lines__lab">VAT rate</span>
                  <Select value={shownRateType(r)} onChange={e => update(r.id, "rateType", e.target.value)} aria-label={`Line ${idx + 1} VAT rate`}>
                    {!shownRateType(r) && <option value="">Choose a rate</option>}
                    {RATE_CHOICES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Select>
                </label>
              )}
              <span className="invc-lines__total invc-money">{fmtMoney(lineTotal, currency)}</span>
              {items.length > 1
                ? <button type="button" className="invc-icon invc-icon--danger" onClick={() => remove(r.id)} aria-label={`Remove line ${idx + 1}`}>✕</button>
                : <span className="invc-lines__spacer" aria-hidden="true" />}
            </div>
          );
        })}
      </div>
      <div className="invc-lines__foot">
        <button type="button" className="invc-add" onClick={add}>+ Add line item</button>
        {/* Item 34: not VAT registered, no VAT line at all (never "VAT £0.00"). */}
        <dl className="invc-totals" data-testid="invoice-editor-totals">
          {registered && <div><dt>Total excluding VAT</dt><dd>{fmtMoney(subtotal, currency)}</dd></div>}
          {registered && <div><dt>Total VAT</dt><dd>{fmtMoney(tax, currency)}</dd></div>}
          <div className="invc-totals__grand"><dt>Total payable</dt><dd>{fmtMoney(total, currency)}</dd></div>
        </dl>
      </div>
    </div>
  );
}

// ─── Invoice form modal ───────────────────────────────────────────────────────
function InvoiceFormModal({ invoice, onSave, onClose, preselectedProjectId, preselectedTimeEntries, preselectedContactId, baseCurrency = "GBP" }) {
  // loaded and loadFailed: the preselected contact waits for the list, then is read by id if absent.
  const { contacts, loaded: contactsLoaded, loadFailed: contactsLoadFailed } = useContacts();
  const { expenses: allExpenses } = useExpenses();
  const isEdit = !!invoice;
  const { vat } = useBusinessVat();   // item 34

  // Item 34: the number is the server counter's; shown, never typed.
  const number = invoice?.number || "";
  const [contactId, setContactId] = useState(invoice?.contactId ?? "__manual__");
  const [contactName, setContactName] = useState(invoice?.contactName ?? "");
  const [contactEmail, setContactEmail] = useState(invoice?.contactEmail ?? "");
  const [contactCo, setContactCo] = useState(invoice?.contactCompany ?? "");
  const [contactAddr, setContactAddr] = useState(invoice?.contactAddress ?? "");
  const [issueDate, setIssueDate] = useState(invoice?.issueDate ?? today);
  const [dueDate, setDueDate] = useState(invoice?.dueDate ?? addDays(today, 30));
  const [currency, setCurrency] = useState(invoice?.currency ?? "GBP");
  const [notes, setNotes] = useState(invoice?.notes ?? "");
  const [isRecurring, setIsRecurring] = useState(!!(invoice?.recurring));
  const [recurFreq, setRecurFreq] = useState(invoice?.recurring?.frequency ?? "monthly");
  const [lineItems, setLineItems] = useState(invoice?.lineItems ?? [emptyLine(vat)]);
  const [taxPoint, setTaxPoint] = useState(invoice?.taxPoint ?? "");
  const [createTask, setCreateTask] = useState(!isEdit);
  const [invoiceStatus, setInvoiceStatus] = useState(invoice?.status ?? "pending");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(invoice?.project_id ?? preselectedProjectId ?? null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [poNumber, setPoNumber] = useState(invoice?.poNumber ?? "");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  // Billable expenses
  const unbilledBillable = (allExpenses || []).filter(e => e.is_billable && !e.is_billed);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [expProjectFilter, setExpProjectFilter] = useState("");

  // Billable time
  const [billableTimeData, setBillableTimeData] = useState(null);   // { hourlyRate, projects: [...] }
  const [billableTimeLoading, setBillableTimeLoading] = useState(false);
  const [showBillableTime, setShowBillableTime] = useState(false);
  const [selectedTimeIds, setSelectedTimeIds] = useState(new Set());

  // Pre-populate from "Bill selected" flow in TimesheetView
  useEffect(() => {
    if (!preselectedTimeEntries?.length) return;
    const grouped = new Map();
    for (const e of preselectedTimeEntries) {
      const pid = e.projectId || 'none';
      if (!grouped.has(pid)) {
        grouped.set(pid, {
          projectId: e.projectId || null,
          projectName: e.projectName || 'Time tracking',
          totalMinutes: 0,
          totalHours: 0,
          suggestedAmount: null,
          entries: [],
        });
      }
      const g = grouped.get(pid);
      g.totalMinutes += e.duration;
      g.entries.push(e);
    }
    [...grouped.values()].forEach(g => { g.totalHours = Math.round((g.totalMinutes / 60) * 100) / 100; });
    setBillableTimeData({ hourlyRate: null, projects: [...grouped.values()] });
    setSelectedTimeIds(new Set(preselectedTimeEntries.map(e => e.id)));
    setShowBillableTime(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch billable time when a CRM contact is selected
  useEffect(() => {
    if (!contactId || contactId === '__manual__' || preselectedTimeEntries?.length) return;
    setBillableTimeData(null);
    setBillableTimeLoading(true);
    api(`/api/invoices/billable-time/${contactId}`)
      .then(r => r.json())
      .then(data => {
        if (data.projects?.length) {
          setBillableTimeData(data);
        } else {
          setBillableTimeData(null);
        }
      })
      .catch(() => setBillableTimeData(null))
      .finally(() => setBillableTimeLoading(false));
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleTimeId(id) {
    setSelectedTimeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAllTime() {
    const all = billableTimeData?.projects.flatMap(p => p.entries.map(e => e.id)) || [];
    setSelectedTimeIds(new Set(all));
  }

  function deselectAllTime() { setSelectedTimeIds(new Set()); }

  // Block D: "Invoice" on a contact opens this form already addressed to them (deep link
  // invoice_new=<contactId>). Applied once, when that contact is in the loaded list.
  //
  // NOT ONLY WHEN IT IS IN THE LIST (Carla, live, 2026-10-21: "Invoice" opened an unaddressed form). The
  // list is the app's copy of her contacts, read once; a contact added since (from an email, the CRM
  // board, Aidey chat), or a list that failed to load, left the form silently blank. Once the list has
  // loaded without them, the contact is read by id; if that fails too, the form says so.
  const appliedPreselect = useRef(false);
  const [fetchedContact, setFetchedContact] = useState(null);
  const [preselectError, setPreselectError] = useState("");
  useEffect(() => {
    if (appliedPreselect.current || isEdit || !preselectedContactId) return;
    if (contacts.some(c => String(c.id) === String(preselectedContactId))) {
      appliedPreselect.current = true;
      pickContact(String(preselectedContactId));
      return;
    }
    if (contactsLoaded === false && !contactsLoadFailed) return;   // still loading: wait for it
    appliedPreselect.current = true;
    api(`/api/contacts/${encodeURIComponent(preselectedContactId)}`)
      .then(r => r.json())
      .then(d => {
        const c = d?.contact;
        if (!c?.id) throw new Error("That contact is no longer available.");
        setFetchedContact(c);
        pickContact(String(c.id), c);
      })
      .catch(e => setPreselectError(`The contact this invoice is for could not be loaded: ${messageFromError(e, "please try again")}. Choose them below or enter their details.`));
  }, [preselectedContactId, contacts, contactsLoaded, contactsLoadFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  function pickContact(id, known = null) {
    setContactId(id);
    if (id === "__manual__") return;
    const c = known || contacts.find(c => String(c.id) === id) || (fetchedContact && String(fetchedContact.id) === id ? fetchedContact : null);
    if (c) {
      setContactName(c.name);
      setContactEmail(c.email || "");
      setContactCo(c.company || "");
      setContactAddr(c.address || "");
      if (c.defaultCurrency) setCurrency(c.defaultCurrency);
    }
  }

  useEffect(() => {
    let alive = true;
    const loadProjects = async () => {
      try {
        const items = await getProjects();
        if (alive) setProjects(Array.isArray(items) ? items : []);
      } catch {
        if (alive) setProjects([]);
      }
    };

    loadProjects();

    const onProjectsUpdated = () => {
      loadProjects();
    };
    window.addEventListener('aidey:projectsUpdated', onProjectsUpdated);

    return () => {
      alive = false;
      window.removeEventListener('aidey:projectsUpdated', onProjectsUpdated);
    };
  }, []);

  async function handleCreateProjectInline() {
    if (!newProjectName.trim()) return;

    try {
      const p = await createProject(newProjectName.trim());
      setProjectId(p.id);
      setProjects(prev => {
        if (prev.some(x => x.id === p.id)) return prev;
        return [...prev, p];
      });
      setNewProjectName("");
      setShowNewProject(false);
    } catch (err) {
      // Block 233: console-only before, so the field kept its text, the panel stayed
      // open and the user was given no reason — a click that did nothing.
      console.error('Failed to create project:', err);
      showErrorToast(messageFromError(err, 'Could not create that project.'));
    }
  }

  const hasValidLineItems = lineItems.some(r => r.description.trim()) || selectedExpenses.length > 0;
  const canSubmit = contactName.trim() && hasValidLineItems && !saving;

  async function handleSave() {
    if (!contactName.trim()) {
      setFormError("Please enter a contact name.");
      return;
    }
    if (!hasValidLineItems) {
      setFormError("Add at least one line item with a description before saving.");
      return;
    }
    // Item 34: a VAT-registered business chooses a rate for every line (the server refuses otherwise).
    if (vat.registered && lineItems.some(r => (r.description.trim() || r.unitPrice > 0) && !shownRateType(r))) {
      setFormError("Choose a VAT rate for every line: 20% standard, 5% reduced, 0% zero-rated, Exempt or Outside the scope.");
      return;
    }
    setFormError("");
    setSaving(true);
    const rec = isRecurring
      ? { frequency: recurFreq, nextDate: addFrequency(issueDate, recurFreq) }
      : null;
    // Convert selected billable expenses to line items: through lib/expenseRebill (2026-10-20), the
    // one rule shared with the server, the expense's NET amount with the VAT added once.
    const expenseLines = selectedExpenses.map(eid => {
      const exp = unbilledBillable.find(e => e.id === eid);
      if (!exp) return null;
      return { id: Date.now() + Math.random(), ...expenseRebillLine(exp, { registered: vat.registered }) };
    }).filter(Boolean);

    // Convert selected billable time entries to line items
    const allTimeEntries = billableTimeData?.projects.flatMap(p => p.entries) || [];
    const selectedTimeEntries = allTimeEntries.filter(e => selectedTimeIds.has(e.id));
    const timeLines = selectedTimeEntries.map(e => ({
      id: Date.now() + Math.random(),
      description: `${e.projectName || 'Time tracking'}${e.note ? `, ${e.note}` : ''}`,
      qty: Math.round((e.duration / 60) * 100) / 100,
      unitPrice: billableTimeData?.hourlyRate || 0,
      ...lineRateFields(vat),   // item 34: the default rate when registered, not a hard-coded 0%
    }));

    try {
      const err = await onSave({
        id: invoice?.id ?? Date.now(),
        contactId: contactId === "__manual__" ? null : contactId,
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        contactCompany: contactCo.trim(),
        contactAddress: contactAddr.trim(),
        issueDate,
        dueDate,
        status: invoiceStatus,
        paidAt: invoice?.paidAt ?? null,
        currency,
        notes: notes.trim(),
        recurring: rec,
        lineItems: [...lineItems.filter(r => r.description.trim() || r.unitPrice > 0), ...expenseLines, ...timeLines],
        createdAt: invoice?.createdAt ?? new Date().toISOString(),
        linkedTaskId: invoice?.linkedTaskId ?? null,
        reminders: invoice?.reminders ?? [],
        project_id: projectId,
        poNumber: poNumber.trim() || null,
        ...(vat.registered ? { taxPoint: taxPoint || null } : {}),
        _selectedExpenseIds: selectedExpenses,
        _selectedTimeIds: [...selectedTimeIds],
        _createTask: createTask && !isEdit,
      });
      // The server's reason, AT the button she pressed (the modal stays open to retry).
      if (err) setFormError(err);
    } finally {
      // Only runs if the modal wasn't unmounted (i.e. save failed and modal stayed open)
      setSaving(false);
    }
  }

  const selectedHours = billableTimeData ? Math.round(
    [...selectedTimeIds].reduce((s, id) => {
      const e = billableTimeData.projects.flatMap(p => p.entries).find(x => x.id === id);
      return s + (e ? e.duration / 60 : 0);
    }, 0) * 100,
  ) / 100 : 0;

  return (
    <Modal open onClose={saving ? undefined : onClose} title={isEdit ? "Edit invoice" : "New invoice"} color="pastel-blue" width={780}
      footer={(
        <>
          <Button variant="tertiary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="confirm" onClick={handleSave} disabled={!canSubmit} aria-busy={saving || undefined}>
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create invoice")}
          </Button>
        </>
      )}>
      <div className="invc-form">
        <div className="invc-form-grid">
          <Field label="Invoice number" hint={number ? (invoice?.status === "draft" ? "Numbered by Aidey, in order." : "Fixed once the invoice is sent.") : undefined}>
            <Input value={number || "Numbered when you save"} readOnly aria-readonly="true" data-testid="invoice-number" />
          </Field>
          <Field label="Currency">
            <Select value={currency} onChange={e => setCurrency(e.target.value)}
              options={CURRENCIES.map(c => ({ value: c.code, label: `${c.code}, ${c.name}` }))} />
          </Field>
          <Field label="Status" hint={isEdit && invoice?.status !== "draft" ? "Change it from the invoice's actions." : undefined}>
            <Select value={invoiceStatus} onChange={e => setInvoiceStatus(e.target.value)} disabled={isEdit && invoice?.status !== "draft"}
              options={[{ value: "draft", label: "Draft" }, { value: "pending", label: "Due" }, { value: "paid", label: "Paid" }, { value: "overdue", label: "Overdue" }]} />
          </Field>
        </div>

        <section className="invc-panel" aria-label="Contact">
          <h3 className="invc-panel__title">Bill to</h3>
          {(contacts.length > 0 || fetchedContact) && (
            <Field label="Contact">
              <Select value={contactId} onChange={e => pickContact(e.target.value)}>
                <option value="__manual__">Enter manually</option>
                {(fetchedContact && !contacts.some(c => String(c.id) === String(fetchedContact.id)) ? [...contacts, fetchedContact] : contacts).map(c => (
                  <option key={c.id} value={String(c.id)}>{c.name}{c.company ? ` (${c.company})` : ""}</option>
                ))}
              </Select>
            </Field>
          )}
          {preselectError && <p className="invc-error" role="alert" data-testid="invoice-preselect-error">{preselectError}</p>}
          <div className="invc-form-grid invc-form-grid--wide">
            <Field label="Name" required><Input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Full name" /></Field>
            <Field label="Email"><Input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="name@example.com" /></Field>
            <Field label="Company" className="is-wide"><Input value={contactCo} onChange={e => setContactCo(e.target.value)} placeholder="Company name" /></Field>
            <Field label="Address" className="is-wide"><Input value={contactAddr} onChange={e => setContactAddr(e.target.value)} placeholder="Street, City, Postcode" /></Field>
          </div>
        </section>

        <div className="invc-form-grid">
          <Field label="Issue date"><Input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} /></Field>
          <Field label="Due date"><Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /></Field>
          {/* Item 34: shown on the invoice only when it differs from the invoice date. */}
          {vat.registered && (
            <Field label="Tax point" hint="Only if the supply was made on a different date from the invoice date.">
              <Input type="date" value={taxPoint || ""} onChange={e => setTaxPoint(e.target.value)} data-testid="invoice-tax-point" />
            </Field>
          )}
          <Field label="PO number" hint="Optional, the client's purchase order reference.">
            <Input value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="e.g. PO-2026-001" />
          </Field>
        </div>

        <div className="invc-project">
          {!showNewProject ? (
            <Field label="Project" hint="Optional.">
              <Select
                value={projectId != null ? String(projectId) : ""}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "__new__") { setShowNewProject(true); }
                  else { setProjectId(val ? Number(val) : null); }
                }}
              >
                <option value="">{projects.length === 0 ? "No projects yet, create one" : "Attach to project (optional)"}</option>
                {projects.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.name}{p.status && p.status !== "active" ? ` (${p.status})` : ""}</option>
                ))}
                <option value="__new__">+ New project…</option>
              </Select>
            </Field>
          ) : (
            <Field label="New project name">
              <Input
                autoFocus
                value={newProjectName}
                onChange={e => setNewProjectName(e.target.value)}
                onKeyDown={async e => {
                  if (e.key === "Enter" && newProjectName.trim()) {
                    await handleCreateProjectInline();
                  }
                  if (e.key === "Escape") { setNewProjectName(""); setShowNewProject(false); }
                }}
                placeholder="Project name…"
              />
            </Field>
          )}
          {showNewProject && (
            <div className="invc-actions">
              <Button variant="confirm" size="sm" onClick={handleCreateProjectInline} disabled={!newProjectName.trim()}>Create</Button>
              <Button variant="tertiary" size="sm" onClick={() => { setNewProjectName(""); setShowNewProject(false); }}>Cancel</Button>
            </div>
          )}
        </div>

        <section aria-label="Line items" className="invc-form__lines">
          <h3 className="invc-panel__title">Line items</h3>
          <LineItemsEditor items={lineItems} onChange={setLineItems} currency={currency} vat={vat} />
        </section>

        <Field label="Notes">
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Payment terms, bank details, etc." />
        </Field>

        <section className="invc-panel" aria-label="Recurring">
          <Checkbox checked={isRecurring} onChange={setIsRecurring} label="Recurring invoice" />
          {isRecurring && (
            <>
              <Field label="Frequency">
                <Select value={recurFreq} onChange={e => setRecurFreq(e.target.value)} options={RECUR_OPTIONS} />
              </Field>
              {/* Block K: this said "Next invoice: <date>", and nothing creates one. The flag is saved on
                  the invoice (the Recurring tab lists it); the repeating templates are a separate
                  feature, hidden until Block 240 (InvoicingLayout). */}
              <p className="invc-small">Marks this invoice as recurring, so it shows under Recurring. Aidey does not create the next one from it yet.</p>
            </>
          )}
        </section>

        {/* Chase task — not applicable for drafts */}
        {invoiceStatus !== "draft" && (
          <section className="invc-panel" aria-label="Payment chase task">
            <Checkbox checked={createTask} onChange={setCreateTask} label="Create a payment chase task on the due date" />
            {createTask && (
              <p className="invc-small">A task is added to your schedule on <strong>{fmtDate(dueDate)}</strong> to confirm payment has come in.</p>
            )}
          </section>
        )}

        {/* Billable time */}
        {(billableTimeData || billableTimeLoading) && (
          <section className="invc-panel" aria-label="Billable time">
            <div className="invc-panel__head">
              <h3 className="invc-panel__title">
                Import billable time
                {billableTimeData && (
                  <span className="invc-small"> ({plural(billableTimeData.projects.reduce((s, p) => s + p.entries.length, 0), "entry").replace("entrys", "entries")} across {plural(billableTimeData.projects.length, "project")})</span>
                )}
              </h3>
              {!billableTimeLoading && (
                <Button variant="tertiary" size="sm" onClick={() => setShowBillableTime(v => !v)} aria-expanded={showBillableTime}>
                  {showBillableTime ? "Hide" : "Show"}
                </Button>
              )}
            </div>
            {billableTimeLoading && <Skeleton lines={2} label="Checking for unbilled time" />}
            {showBillableTime && billableTimeData && (
              <>
                <div className="invc-actions">
                  <Button variant="tertiary" size="sm" onClick={selectAllTime}>All</Button>
                  <Button variant="tertiary" size="sm" onClick={deselectAllTime}>None</Button>
                  {billableTimeData.hourlyRate && (
                    <span className="invc-small">Rate: {fmtMoney(billableTimeData.hourlyRate, currency)} an hour</span>
                  )}
                </div>
                {billableTimeData.projects.map(proj => (
                  <div key={proj.projectId || 'none'} className="invc-picks">
                    <p className="invc-picks__head">
                      {proj.projectName}, {proj.totalHours}h total
                      {proj.suggestedAmount ? ` · ${fmtMoney(proj.suggestedAmount, currency)} suggested` : ""}
                    </p>
                    {proj.entries.map(e => {
                      const hrs = Math.round((e.duration / 60) * 100) / 100;
                      return (
                        <div key={e.id} className="invc-pick">
                          <Checkbox checked={selectedTimeIds.has(e.id)} onChange={() => toggleTimeId(e.id)} label={e.note || "Time tracked"} />
                          <span className="invc-small">{shortDate(e.startTime)}</span>
                          <span className="invc-money">{hrs}h</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {selectedTimeIds.size > 0 && (
                  <p className="invc-small">
                    {selectedTimeIds.size} {selectedTimeIds.size === 1 ? "entry" : "entries"} selected · {selectedHours}h
                    {billableTimeData.hourlyRate ? ` · ${fmtMoney(selectedHours * billableTimeData.hourlyRate, currency)}` : ""} will be added as line items
                  </p>
                )}
              </>
            )}
          </section>
        )}

        {/* Billable expenses: logged in the business's own currency, so priced in it. */}
        {unbilledBillable.length > 0 && (
          <section className="invc-panel" aria-label="Billable expenses">
            <h3 className="invc-panel__title">Add billable expenses <span className="invc-small">({unbilledBillable.length} unbilled)</span></h3>
            {projects.length > 0 && (
              <Field label="Show expenses for">
                <Select value={expProjectFilter} onChange={e => setExpProjectFilter(e.target.value)}>
                  <option value="">All projects</option>
                  {projects.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </Select>
              </Field>
            )}
            <div className="invc-picks">
              {unbilledBillable
                .filter(e => !expProjectFilter || String(e.project_id) === expProjectFilter)
                .map(exp => (
                  <div key={exp.id} className="invc-pick">
                    <Checkbox
                      checked={selectedExpenses.includes(exp.id)}
                      onChange={(on) => setSelectedExpenses(prev => on ? [...prev, exp.id] : prev.filter(id => id !== exp.id))}
                      label={exp.title}
                    />
                    <span className="invc-small">{exp.category}</span>
                    <span className="invc-money">{fmtMoney(exp.amount, baseCurrency)}</span>
                  </div>
                ))}
            </div>
            {selectedExpenses.length > 0 && (
              <p className="invc-small">
                {selectedExpenses.length} selected · {fmtMoney(unbilledBillable.filter(e => selectedExpenses.includes(e.id)).reduce((s, e) => s + Number(e.amount), 0), baseCurrency)} will be added as line items
              </p>
            )}
          </section>
        )}

        {formError && <Banner tone="problem">{formError}</Banner>}
      </div>
    </Modal>
  );
}

// ─── Invoice detail ───────────────────────────────────────────────────────────
// Block H-5 (Carla's decision 4, 2026-10-10): the refunds on an invoice, and recording one. Only a
// paid (or partly refunded) invoice can take a refund; the server enforces the same and keeps the
// amount, the rows and the status together (lib/invoiceRefunds.js).
function RefundPanel({ invoice, total, gated, readOnly, onRefunded }) {
  const code = invoice.currency;
  const refunds = Array.isArray(invoice.refunds) ? invoice.refunds : [];
  const canRecord = !gated && !readOnly && (invoice.status === "paid" || invoice.status === "partially_refunded");
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [day, setDay] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const left = Math.max(0, Number(total || 0) - Number(invoice.refundedAmount || 0));
  if (!refunds.length && !canRecord) return null;

  async function save() {
    setSaving(true); setError(null);
    try {
      const res = await api(`/api/invoices/${invoice.id}/refunds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: Number(amount), refundedOn: day, note: note.trim() || null }),
      });
      const data = await res.json();
      if (!data.invoice) throw new Error("The refund was not recorded. Try again.");
      setOpen(false); setAmount(""); setNote("");
      onRefunded?.(data.invoice);
    } catch (err) {
      setError(messageFromError(err, "The refund was not recorded. Try again."));
    } finally { setSaving(false); }
  }

  const fmtDay = (d) => { const x = new Date(`${d}T12:00:00`); return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); };
  return (
    <section className="invc-panel" aria-label="Refunds">
      <div className="invc-panel__head">
        <h3 className="invc-panel__title">Refunds{refunds.length ? `: ${fmtMoney(invoice.refundedAmount, code)} of ${fmtMoney(total, code)}` : ""}</h3>
        {canRecord && !open && <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>Record a refund</Button>}
      </div>
      {refunds.length > 0 && (
        <ul className="invc-refunds">
          {refunds.map(r => (
            <li key={r.id}>
              <strong className="invc-money">{fmtMoney(r.amount, code)}</strong>
              <span>{fmtDay(r.refundedOn)}</span>
              <span className="invc-small">{r.source === "stripe" ? "Refunded in Stripe" : (r.note || "Recorded by you")}</span>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <Reveal className="invc-form">
          <Field label="Amount refunded" hint={`Up to ${fmtMoney(left, code)} is left to refund.`}>
            <Input id={`refund-amount-${invoice.id}`} type="number" inputMode="decimal" min="0.01" step="0.01" max={left} value={amount}
              onChange={e => setAmount(e.target.value)} placeholder={left.toFixed(2)} />
          </Field>
          <Field label="Date refunded">
            <Input id={`refund-day-${invoice.id}`} type="date" value={day} onChange={e => setDay(e.target.value)} />
          </Field>
          <Field label="Note (optional)">
            <Input id={`refund-note-${invoice.id}`} value={note} onChange={e => setNote(e.target.value)} maxLength={500} placeholder="For example, one item returned" />
          </Field>
          {error && <p role="alert" className="invc-error">{error}</p>}
          <div className="invc-actions">
            <Button variant="confirm" onClick={save} disabled={saving || !(Number(amount) > 0) || !day}>{saving ? "Saving…" : "Save refund"}</Button>
            <Button variant="tertiary" onClick={() => { setOpen(false); setError(null); }}>Cancel</Button>
          </div>
        </Reveal>
      )}
    </section>
  );
}

// readOnly: a colleague's invoice in a finance member's or owner's ledger: sending, refunding and
// deleting are the author's own and are not offered. canChange: the viewer may still edit it and mark
// it paid (their own, or they are owner/finance: Carla's decision 3, Block K; the server records who).
function InvoiceDetailModal({ invoice, projects, paymentDetails, bookingUsername, gated, canApprove, readOnly = false, canChange = !readOnly, xeroAllowed = false, onSubmitForApproval, onApprove, onReject, onClose, onEdit, onMarkPaid, onVoid, onUnvoid, onDelete, onUpdateReminders, onUpdateProject, onSent, onRefunded }) {
  const totals = calcTotals(invoice.lineItems, invoice.currency);
  const shown = invoiceDisplayStatus(invoice);
  const st = STATUS[shown] || STATUS.pending;   // Block H-5: the list's own word
  // TeamBooks approval state (camelCase — serializeInvoice spreads the raw model).
  const approvalStatus = invoice.approvalStatus || "approved";
  const isPending  = approvalStatus === "pending";
  const isRejected = approvalStatus === "rejected";
  const [reminders, setReminders] = useState(invoice.reminders ?? []);
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null);
  const [sendUpgradeUrl, setSendUpgradeUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const paid = isPaidStatus(invoice.status);

  async function downloadPdf() {
    setDownloading(true);
    setPdfError(null);
    const err = await downloadInvoicePdf(invoice);
    if (err) setPdfError(err);
    setDownloading(false);
  }

  function copyInvoiceLink() {
    const url = bookingUsername
      ? `${window.location.origin}/invoice/${encodeURIComponent(bookingUsername)}/${encodeURIComponent(invoice.number)}`
      : null;
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => showErrorToast("That link was not copied. Please try again."));
  }

  // AF-005 (first completion audit A5, P0). This used to build the client's
  // email HERE, in the browser: it linked to /invoice/{username}/{number} (a URL
  // that now shows "this link has moved"), pasted the client's name, the line
  // descriptions and the notes raw into HTML, pre-flighted the pay button
  // against a route that 404s so it never appeared, and never marked the
  // invoice sent. The server now sends it — the same template, secure link and
  // branding as every other invoice email — and only a real send marks it sent.
  // If it fails, the invoice is unchanged and the server's reason is shown.
  async function sendViaEmail() {
    setSending(true);
    setSendMsg(null);
    setSendUpgradeUrl(null);
    try {
      const res = await api(`/api/invoices/${invoice.id}/send`, { method: 'POST' });
      const data = await res.json();
      setSendMsg(data.resent ? '✓ Sent again' : '✓ Sent');
      if (data.invoice?.status) onSent?.(invoice.id, data.invoice.status);
    } catch (err) {
      setSendMsg(messageFromError(err, 'Failed to send'));
      // UI-24 item 19: a plan or monthly-allowance refusal names where to upgrade; shown here, at Send.
      const up = err?.body?.upgradeUrl;
      if (typeof up === 'string' && up.startsWith('/')) setSendUpgradeUrl(up);
    }
    finally { setSending(false); }
  }

  function togglePreset(preset) {
    const sendDate = reminderDate(invoice.dueDate, preset.daysOffset);
    const exists = reminders.find(r => r.daysOffset === preset.daysOffset);
    let updated;
    if (exists) {
      updated = reminders.filter(r => r.daysOffset !== preset.daysOffset);
    } else {
      updated = [...reminders, { daysOffset: preset.daysOffset, label: preset.label, sendDate, status: "pending" }];
    }
    updated.sort((a, b) => a.daysOffset - b.daysOffset);
    setReminders(updated);
    onUpdateReminders(invoice.id, updated);
  }

  function cancelReminder(daysOffset) {
    const updated = reminders.map(r => r.daysOffset === daysOffset ? { ...r, status: "cancelled" } : r);
    setReminders(updated);
    onUpdateReminders(invoice.id, updated);
  }

  const pendingCount = reminders.filter(r => r.status === "pending").length;
  const live = reminders.filter(r => r.status !== "cancelled");
  const canSend = invoice.contactEmail && !paid && !gated && !readOnly;
  const canPay = !paid && canChange;
  const projectName = (projects || []).find(p => String(p.id) === String(invoice.project_id))?.name;

  return (
    <Modal open onClose={onClose} title={invoice.number} color={st.color} width={760}>
      <div className="invc-detail">
        <div className="invc-pills">
          <StatusPill tone={st.tone}>{st.label}</StatusPill>
          {isPending && <StatusPill tone="pending">Awaiting approval</StatusPill>}
          {isRejected && <StatusPill tone="warning">Rejected</StatusPill>}
          {invoice.recurring && <span className="invc-pill">Recurring, {invoice.recurring.frequency}</span>}
          {invoice.linkedTaskId && <span className="invc-pill">Chase task active</span>}
          {pendingCount > 0 && <span className="invc-pill">{plural(pendingCount, "reminder")} set</span>}
        </div>

        {/* readOnly = not mine (send, refund, delete stay the author's); canChange = I may edit and mark
            it paid (mine, or I am owner/finance: Carla's decision 3, Block K). */}
        {readOnly && canChange && (
          <Banner tone="info">A colleague's invoice. As an owner or finance member you can change it and mark it paid; your name is recorded. Only they can send, refund or delete it.</Banner>
        )}
        {readOnly && !canChange && (
          <Banner tone="info">A colleague's invoice. You can view and download it; only they can change, send or delete it.</Banner>
        )}

        {/* Rejection reason banner (submitter sees why + can edit/resubmit) */}
        {isRejected && invoice.rejectionReason && (
          <Banner tone="problem"><strong>Rejected:</strong> {invoice.rejectionReason}</Banner>
        )}

        {!canChange ? (
          <p className="invc-muted">Project: {projectName || "None"}</p>
        ) : (
          <Field label="Project">
            <Select
              value={invoice.project_id != null ? String(invoice.project_id) : ""}
              onChange={e => onUpdateProject && onUpdateProject(invoice.id, e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">No project</option>
              {(projects || []).map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </Select>
          </Field>
        )}

        {/* Item 34: THE ONE DOCUMENT, as the client's page, the portal and the PDF show it
            (GET /api/invoices/:id/document, lib/invoiceDocument). This view had its own table, its
            own sums and a "Tax %" column: a second renderer. */}
        <OwnerInvoiceDocument invoice={invoice} overdue={shown === "overdue"} />

        {paymentDetails && (
          <section className="invc-panel invc-panel--plain" aria-label="Payment details">
            <h3 className="invc-panel__title">Payment details</h3>
            <p className="invc-pre">{paymentDetails}</p>
          </section>
        )}

        {/* Reminders. Block K: nothing emails these yet (no sender reads them); it says so plainly. */}
        {!paid && (
          <section className="invc-panel" aria-label="Email reminders">
            <div className="invc-panel__head">
              <h3 className="invc-panel__title">Email reminders</h3>
            </div>
            <p className="invc-small">Aidey does not email these yet. Your choices are saved on the invoice.</p>
            <div className="ui-chips invc-chips" role="group" aria-label="Reminder times">
              {REMINDER_PRESETS.map(preset => {
                const active = reminders.find(r => r.daysOffset === preset.daysOffset);
                const isCancelled = active?.status === "cancelled";
                return (
                  <Chip key={preset.daysOffset} selected={!!active && !isCancelled} disabled={isCancelled || !canChange}
                    onClick={() => !isCancelled && canChange && togglePreset(preset)}>
                    {preset.label}
                  </Chip>
                );
              })}
            </div>
            {live.length > 0 && (
              <ul className="invc-reminders">
                {live.map(r => (
                  <li key={r.daysOffset}>
                    <StatusPill tone={r.status === "sent" ? "success" : "pending"}>{r.status === "sent" ? "Sent" : "Scheduled"}</StatusPill>
                    <span>{r.label} · {fmtDate(r.sendDate)}</span>
                    {r.status === "pending" && canChange && (
                      <button type="button" className="invc-icon" onClick={() => cancelReminder(r.daysOffset)} aria-label={`Cancel the reminder ${r.label}`}>✕</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Block H-5 (Carla's decision 4): refunds on a paid invoice. */}
        <RefundPanel invoice={invoice} total={totals.total} gated={gated} readOnly={readOnly} onRefunded={onRefunded} />

        {/* M2 (2026-10-12): where this invoice is in Xero. Renders nothing unless the business has Xero connected.
            UI-24 item 19: the Xero push is Pro, so on Basics it is not drawn or asked for (the server refuses it too). */}
        {xeroAllowed && <XeroSyncPanel invoice={invoice} canChange={canChange} />}

        {/* ── WHICH MAILBOX WILL SEND THIS (item 7, 2026-10-06) ─────────────────────────────
            Carla, 2026-09-21: "show what sends before it goes." It sits ABOVE the Send button and
            not in a panel header, because the branding error taught that a message belongs AT the
            control that it is about, she was at the bottom by the button while the message was at
            the top of the panel. One line; the component asks the one endpoint. */}
        {canSend && <SendingFrom to={invoice.contactEmail} />}

        {/* The decisions: send, approval, paid. */}
        {(canSend || (gated && !readOnly && !paid) || (canApprove && isPending) || canPay) && (
          <div className="invc-actions invc-actions--main">
            {/* Direct client-send: hidden for gated members (they submit for approval). */}
            {canSend && (
              <Button variant="confirm" onClick={sendViaEmail} disabled={sending} aria-busy={sending || undefined}>
                {sending ? "Sending…" : (invoice.status === "draft" ? "Send invoice" : "Send invoice again")}
              </Button>
            )}
            {/* Gated member: explicit Submit-for-approval (Option B); locked while pending. */}
            {gated && !readOnly && isPending && <span className="invc-small">Waiting for an owner or finance member to approve it.</span>}
            {gated && !readOnly && !isPending && !paid && (
              <Button variant="confirm" onClick={() => onSubmitForApproval(invoice.id)}>
                {isRejected ? "Resubmit for approval" : "Submit for approval"}
              </Button>
            )}
            {/* Owner/finance: approve (→ sends) or reject a pending invoice. */}
            {canApprove && isPending && (
              <>
                <Button variant="confirm" onClick={() => onApprove(invoice.id)}>Approve and send</Button>
                <Button variant="destructive" onClick={() => onReject(invoice.id)}>Reject</Button>
              </>
            )}
            {canPay && <Button variant="secondary" onClick={() => onMarkPaid(invoice.id)}>Mark as paid</Button>}
          </div>
        )}
        {sendMsg && <p className={sendMsg.startsWith("✓") ? "invc-ok" : "invc-error"} role="status">{sendMsg}</p>}
        {sendMsg && sendUpgradeUrl && <p className="invc-error"><a href={sendUpgradeUrl}>See plans</a></p>}

        {/* ── 1.4: A PLAIN DOWNLOAD, AND THE SAME DOCUMENT THE CLIENT GETS ──────────────────
            Carla: "the invoice PDF only opens the print dialogue, no plain download (awkward on
            a phone)." A real PDF has existed since Block 167 (her branding, the line items, a total
            built from amountDueText so it cannot disagree with what Stripe charges); this button
            downloads THAT, the same document the client gets. Print stays, because printing is a
            different act from saving a file and she may want either. */}
        <div className="invc-actions">
          <Button variant="secondary" onClick={downloadPdf} disabled={downloading} aria-busy={downloading || undefined}>
            {downloading ? "Preparing…" : "Download PDF"}
          </Button>
          <Button variant="tertiary" onClick={() => printInvoicePdf(invoice)}>Print</Button>
          {bookingUsername && (
            <Button variant="tertiary" onClick={copyInvoiceLink}>{copied ? "✓ Copied" : "Copy link"}</Button>
          )}
          {/* Editing a submitted invoice is locked for the member until it's decided. */}
          {canChange && !(gated && isPending) && (
            <Button variant="tertiary" onClick={() => { onEdit(invoice); onClose(); }}>Edit</Button>
          )}
          {/* Block 171: cancel an active invoice, or Restore a cancelled one. UI-24 item 19, decision 2
              (Carla, 2026-10-20): "Cancel invoice" (it was "Void"), the words the server uses when it
              refuses to delete a sent one; Delete is offered only for a draft that was never sent. */}
          {canChange && !paid && invoice.status !== "cancelled" && (
            <Button variant="tertiary" onClick={() => onVoid(invoice.id)}>Cancel invoice</Button>
          )}
          {canChange && invoice.status === "cancelled" && (
            <Button variant="tertiary" onClick={() => onUnvoid(invoice.id)}>Restore</Button>
          )}
          {!readOnly && canDeleteInvoice(invoice) && <Button variant="destructive" onClick={() => onDelete(invoice.id)}>Delete</Button>}
        </div>
        {pdfError && <p data-testid="pdf-error" className="invc-error" role="alert">{pdfError}</p>}
      </div>
    </Modal>
  );
}

// ─── The owner's view of the one document (item 34) ──────────────────────────
// Read from the server each time the invoice changes, so it is what the client sees, never a copy
// built here. A failed read says so, with a retry, rather than showing an invoice it does not have.
function OwnerInvoiceDocument({ invoice, overdue }) {
  const [doc, setDoc] = useState(null);
  const [failed, setFailed] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setFailed(null);
    api(`/api/invoices/${invoice.id}/document`)
      .then(r => r.json())
      .then(d => { if (live) { if (d && d.document) setDoc(d.document); else setFailed("The invoice could not be shown. Please try again."); } })
      .catch(err => { if (live) setFailed(messageFromError(err, "The invoice could not be shown. Please try again.")); });
    return () => { live = false; };
  }, [invoice.id, invoice.updatedAt, invoice.status, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  if (failed) return <ErrorState title="The invoice did not load" message={failed} onRetry={() => setAttempt(a => a + 1)} />;
  if (!doc) return <Skeleton lines={6} label="Loading the invoice" />;
  return <div className="invc-document" data-testid="owner-invoice-document"><InvoiceBody invoice={invoice} doc={doc} overdue={overdue} /></div>;
}

// ─── The reject reason (was window.prompt) ───────────────────────────────────
function RejectModal({ invoice, onClose, onReject }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  async function go() {
    setBusy(true); setError(null);
    try { await onReject(invoice.id, reason.trim()); showSuccessToast("Invoice rejected."); onClose(); }
    catch (err) { setError(messageFromError(err, "That invoice was not rejected. Please try again.")); setBusy(false); }
  }
  return (
    <Modal open onClose={busy ? undefined : onClose} title={`Reject ${invoice.number}?`} color="pastel-coral" width={480}
      footer={(
        <>
          <Button variant="tertiary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="delete" onClick={go} disabled={busy} aria-busy={busy || undefined}>{busy ? "Rejecting…" : "Reject"}</Button>
        </>
      )}>
      <div className="invc-form">
        <p className="invc-muted">It goes back to its author as a draft, with your reason.</p>
        <Field label="Reason (optional)"><Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} /></Field>
        {error && <p role="alert" className="invc-error">{error}</p>}
      </div>
    </Modal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function InvoicingDashboard({ initialTab = "All", onOpenProjectFinancials, openFormWithProjectId, onFormOpened, billTimeEntries, onBillTimeFormOpened }) {
  const { t } = useTranslation('common');
  const { currentUser } = useCurrentUser();
  const paymentDetails = currentUser?.preferences?.invoicingPrefs?.paymentDetails || '';
  const bookingUsername = currentUser?.bookingUsername || null;
  const { invoices, setInvoices, refresh: refreshInvoices, loadFailed: invoicesLoadFailed, loaded } = useInvoices();
  const { refresh: refreshExpenses } = useExpenses();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [preselectedProjectId, setPreselectedProjectId] = useState(null);
  const [preselectedContactId, setPreselectedContactId] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [confirm, setConfirm] = useState(null);     // { title, message, confirmLabel, destructive, run }
  const [restoreEmail, setRestoreEmail] = useState(false);   // decision 2: the Restore dialog's unticked option
  const restoreEmailRef = useRef(false);                     // read by run(), which closes over an older render
  const [rejecting, setRejecting] = useState(null); // the invoice being rejected
  // Block D20: an invoice deep-link opens that invoice's detail (once the list is loaded).
  // Item 8 (2026-10-12): once loaded, the invoice opens, or "This item is no longer available".
  useDeepLinkItem('invoice', {
    ready: loaded !== false && !invoicesLoadFailed,
    find: (id) => invoices.find(i => String(i.id) === id),
    open: (inv) => setViewInvoice(inv),
  });
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const phone = useBreakpoint() === "phone";   // fresh-eyes item 21: search first, the rest behind Filter
  const [allProjects, setAllProjects] = useState([]);

  const projectsLookup = Object.fromEntries(allProjects.map(p => [p.id, p.name]));
  // The context's own `loaded`; a provider without one (older tests) counts as loaded.
  const loading = loaded === false && !invoicesLoadFailed;

  useEffect(() => {
    let alive = true;

    const loadProjects = async () => {
      try {
        const projects = await getProjects();
        if (alive) setAllProjects(Array.isArray(projects) ? projects : []);
      } catch {
        if (alive) setAllProjects([]);
      }
    };

    loadProjects();

    const onProjectsUpdated = () => {
      loadProjects();
    };
    window.addEventListener('aidey:projectsUpdated', onProjectsUpdated);

    return () => {
      alive = false;
      window.removeEventListener('aidey:projectsUpdated', onProjectsUpdated);
    };
  }, []);

  // UI-24 item 19: Basics sends 10 a calendar month. The count is the SERVER's (the one the send
  // doors use), read again whenever an invoice leaves draft, so it never goes stale after a send.
  // A failed read shows nothing rather than a guessed number; a refusal at Send still says why.
  const [allowance, setAllowance] = useState(null);
  const leftDraftKey = (invoices || []).filter(i => i.status !== 'draft').map(i => i.id).join(',');
  useEffect(() => {
    let alive = true;
    api('/api/invoices/send-allowance')
      .then(r => r.json())
      .then(a => { if (alive) setAllowance(a && !a.unlimited && Number.isFinite(a.limit) && Number.isFinite(a.used) ? a : null); })
      .catch(() => { if (alive) setAllowance(null); });
    return () => { alive = false; };
  }, [leftDraftKey]);

  const [preselectedTimeEntries, setPreselectedTimeEntries] = useState(null);

  // Block B: "+ New invoice" on Financial overview opens the form here (deep link invoice_new).
  const [dlNewInvoice, dlClearNewInvoice] = useDeepLinkTarget('invoice_new');
  useEffect(() => {
    if (!dlNewInvoice) return;
    setEditTarget(null);
    setPreselectedProjectId(null);
    // "1" is Financial overview's plain "+ New invoice"; anything else is the contact it is for.
    setPreselectedContactId(dlNewInvoice !== '1' ? dlNewInvoice : null);
    setShowForm(true);
    dlClearNewInvoice();
    // Answered: the ?invoice_new= leaves the address, so a reload or a remount does not open a second form.
    clearPendingDeepLink();
  }, [dlNewInvoice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open invoice form when a project shortcut requests it
  useEffect(() => {
    if (openFormWithProjectId != null) {
      setEditTarget(null);
      setPreselectedProjectId(openFormWithProjectId);
      setShowForm(true);
      if (onFormOpened) onFormOpened();
    }
  }, [openFormWithProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open invoice form with pre-selected time entries from TimesheetView
  useEffect(() => {
    if (billTimeEntries?.length) {
      setEditTarget(null);
      setPreselectedProjectId(null);
      setPreselectedTimeEntries(billTimeEntries);
      setShowForm(true);
      if (onBillTimeFormOpened) onBillTimeFormOpened();
    }
  }, [billTimeEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // TeamBooks invoice approval. canApproveExpenses = owner/finance (the same
  // financialStanding privilege that gates invoices). A plain business member
  // (has a business but can't approve) is "gated": they submit for approval
  // instead of sending directly. Solo users have no teamId → gated = false, so
  // no approval affordances appear at all.
  const canApproveInvoices = !!currentUser?.canApproveExpenses;
  const isBusinessMember = !!(currentUser?.teamId || currentUser?.isCoveredMember);
  const invoiceGated = isBusinessMember && !canApproveInvoices;
  // Block K: whose invoice. Send, refund and delete stay the author's own (isMine). Carla's decision 3
  // (Block K): the owner and finance may also EDIT and MARK PAID a colleague's (canChange); the server
  // records who changed it.
  const isMine = (inv) => !inv?.userId || !currentUser?.id || String(inv.userId) === String(currentUser.id);
  const canChange = (inv) => isMine(inv) || canApproveInvoices;

  // ── Every write waits for the server and shows ITS answer ───────────────────────────────────
  // Block K: Mark paid, Void, Restore, Approve, Reject and Submit used to change the row and toast
  // success before the request, and a refusal went to the console. The row now changes to what the
  // server returns, and a refusal is shown.
  function replaceRow(serverInvoice) {
    const n = fromServer(serverInvoice);
    setInvoices(prev => prev.map(i => i.id === n.id ? { ...i, ...n } : i));
    setViewInvoice(v => (v && v.id === n.id ? { ...v, ...n } : v));
    return n;
  }
  async function putInvoice(id, body) {
    const res = await api(`/api/invoices/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    const data = await res.json();
    if (!data?.invoice) throw new Error("The server did not confirm that change. Please check the invoice.");
    return replaceRow(data.invoice);
  }

  async function submitInvoiceForApproval(id) {
    // A member's "Submit for approval" reuses PUT status:'sent'; the backend
    // gate converts it to approval_status='pending' (stays a draft, no send).
    try {
      await putInvoice(id, { status: 'sent' });
      setViewInvoice(null);
      showSuccessToast("Invoice submitted for approval.");
    } catch (err) {
      showErrorToast(messageFromError(err, "That invoice was not submitted. Please try again."));
    }
  }

  async function approveInvoice(id) {
    try {
      const res = await api(`/api/invoices/${id}/approve`, { method: 'PUT' });
      const d = await res.json();
      if (!d?.invoice) throw new Error("The server did not confirm the approval. Please check the invoice.");
      const n = replaceRow(d.invoice);
      setViewInvoice(null);
      showSuccessToast(n.status === 'sent' ? "Invoice approved and sent." : "Invoice approved.");
    } catch (err) {
      showErrorToast(messageFromError(err, "That invoice was not approved. Please try again."));
    }
  }

  // Throws on failure: RejectModal shows the server's reason at its button.
  async function rejectInvoice(id, reason) {
    const res = await api(`/api/invoices/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason: reason || null }) });
    const d = await res.json();
    if (!d?.invoice) throw new Error("The server did not confirm the rejection. Please check the invoice.");
    replaceRow(d.invoice);
    setViewInvoice(null);
  }

  // Returns null on success, or the server's reason (the form shows it at its button).
  async function saveInvoice(inv) {
    const { _createTask, _selectedExpenseIds, _selectedTimeIds, ...clean } = inv;

    // Build API payload (map project_id → projectId for backend)
    const payload = { ...clean, projectId: clean.project_id };
    delete payload.project_id;

    const isEdit = invoices.some(i => i.id === clean.id);
    let saved;
    try {
      const res = isEdit
        ? await api(`/api/invoices/${clean.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await api('/api/invoices', { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data?.invoice) throw new Error("The server did not confirm the save. Please check your invoices.");
      saved = fromServer(data.invoice);
    } catch (err) {
      console.error('Failed to save invoice:', err);
      const msg = messageFromError(err, "That invoice was not saved. Please try again.");
      showErrorToast(`Could not save invoice: ${msg}`);
      return msg;   // the modal stays open so she can see why and retry
    }
    if (isEdit) setInvoices(prev => prev.map(i => i.id === clean.id ? { ...i, ...saved } : i));
    else setInvoices(prev => [saved, ...prev]);

    // Only once the invoice EXISTS: mark what it bills as billed (fire-and-forget, as before).
    if (_selectedExpenseIds?.length) {
      api('/api/expenses/bulk-billed', {
        method: 'PUT',
        body: JSON.stringify({ ids: _selectedExpenseIds }),
      }).then(() => refreshExpenses()).catch(err => console.error('Failed to mark expenses billed:', err));
    }
    if (_selectedTimeIds?.length) {
      api('/api/time-entries/bulk-billed', {
        method: 'PUT',
        body: JSON.stringify({ ids: _selectedTimeIds }),
      }).catch(err => console.error('Failed to mark time entries billed:', err));
    }

    // The payment chase task, also only once the invoice exists (Block K: it was created FIRST, so a
    // refused save left a chase task for an invoice that did not exist). Its own try, so a failure
    // here never undoes the saved invoice; she is told the task was not made.
    let taskNote = '';
    if (_createTask && !saved.linkedTaskId) {
      try {
        const taskId = await createChaseTask(saved);
        if (taskId) {
          await putInvoice(saved.id, { linkedTaskId: taskId });
          window.dispatchEvent(new Event('aidey:tasksUpdated'));
          taskNote = ' Chase task created.';
        }
      } catch (err) {
        console.error('Chase task creation failed:', err);
        taskNote = ' The chase task was not created.';
      }
    }

    // The success is said only once the invoice is saved (above) and inside this try (FalseSuccess guard).
    try {
      setShowForm(false);
      setEditTarget(null);
      showSuccessToast(`Invoice ${saved.number} ${isEdit ? 'updated' : 'created'}.${taskNote}`);
    } catch (err) { console.error(err); }
    return null;
  }

  async function doMarkPaid(id) {
    const inv = invoices.find(i => i.id === id);
    // CANONICAL FORMAT: a FULL ISO TIMESTAMP, not a date.
    //
    // invoices.paid_at is TEXT and held both: this line wrote 10 characters while
    // lib/invoicePayments.js (the Stripe card path) wrote 24. lib/healthScore.js then compared the
    // two as instants, so a client who paid by card at 14:00 on the due date read as LATE while the
    // same invoice marked by hand read as on time — the clients who paid most conveniently scored
    // worst. The comparison is fixed in lib/invoiceDates.js; this stops NEW rows inheriting the
    // split, because a timestamp column is the kind of thing the next instrument gets built on.
    //
    // The timestamp wins because "when did they pay" is a real question and `slice(0, 10)` throws
    // away information that cannot be recovered. Nothing depends on date-only: every reader either
    // displays it (fmtDate and ukDate already handle both) or wants a real instant.
    const paidAt = new Date().toISOString();
    await putInvoice(id, { status: 'paid', paidAt });
    if (inv?.linkedTaskId) completeLinkedTask(inv.linkedTaskId);
    showSuccessToast("Invoice marked as paid.");
  }

  function markPaid(id) {
    const inv = invoices.find(i => i.id === id);
    // Block 171: moving a cancelled invoice to paid must be deliberate — a voided invoice being
    // silently marked paid would be wrong. Name exactly what is happening before proceeding.
    if (inv?.status === 'cancelled') {
      setConfirm({
        title: `Mark ${inv.number} as paid?`,
        message: `Invoice ${inv.number} is cancelled. Marking it as paid un-voids it and records it as paid.`,
        confirmLabel: 'Mark as paid', destructive: false,
        run: () => doMarkPaid(id),
      });
      return;
    }
    doMarkPaid(id).catch(err => showErrorToast(messageFromError(err, "That invoice was not marked as paid. Please try again.")));
  }

  // Block 171: Cancel (was "Void") — mark an invoice cancelled. Confirm-gated. A cancelled invoice can
  // no longer be paid online (backend allow-list), but it is NOT deleted and can be restored. Since
  // UI-24 item 19 (decision 2) it is the only way out for a sent invoice: it cannot be deleted.
  function voidInvoice(id) {
    const inv = invoices.find(i => i.id === id);
    setConfirm({
      title: `Cancel ${inv?.number || 'this invoice'}?`,
      message: "It will be marked cancelled and can no longer be paid online. You can restore it later.",
      confirmLabel: 'Cancel invoice', destructive: true,
      run: async () => { await putInvoice(id, { status: 'cancelled' }); showSuccessToast("Invoice cancelled."); },
    });
  }

  // Un-void: move a cancelled invoice back to Sent. Confirm-gated and names what is happening,
  // because leaving 'cancelled' is a deliberate act (the guard blocks online payment while voided).
  // Carla's decision 2 (2026-10-12): silent by default. An UNTICKED "Email the client again" is offered on
  // the person's own invoice only (sending stays the author's; the server refuses it for anyone else).
  function unvoidInvoice(id) {
    const inv = invoices.find(i => i.id === id);
    const mine = isMine(inv);
    restoreEmailRef.current = false;
    setRestoreEmail(false);
    setConfirm({
      title: `Restore ${inv?.number || 'this invoice'}?`,
      message: mine
        ? "It will move back to Sent and can be paid online again. The client is not emailed unless you tick the box."
        : "It will move back to Sent and can be paid online again. The client is not emailed.",
      confirmLabel: 'Restore', destructive: false,
      option: mine ? 'restoreEmail' : null,
      run: async () => {
        const emailClient = mine && restoreEmailRef.current === true;
        await putInvoice(id, emailClient ? { status: 'sent', emailClient: true } : { status: 'sent' });
        showSuccessToast(emailClient ? "Invoice restored and emailed to the client." : "Invoice restored.");
      },
    });
  }

  // Shown at once (a chip or a select should feel instant); a refusal is shown and the list reloaded.
  async function updateReminders(invoiceId, reminders) {
    setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, reminders } : i));
    setViewInvoice(v => v && v.id === invoiceId ? { ...v, reminders } : v);
    try { await putInvoice(invoiceId, { reminders }); }
    catch (err) { showErrorToast(messageFromError(err, "Those reminders were not saved.")); refreshInvoices(); }
  }

  async function updateProject(invoiceId, project_id) {
    setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, project_id } : i));
    setViewInvoice(v => v && v.id === invoiceId ? { ...v, project_id } : v);
    try { await putInvoice(invoiceId, { projectId: project_id }); }
    catch (err) { showErrorToast(messageFromError(err, "The project was not changed.")); refreshInvoices(); }
  }

  // Decision 7: a delete waits behind Undo (6 seconds) before anything is sent; the server writes the
  // deletion log once it has really happened (routes/invoices.js).
  const del = useDelayedDelete({
    send: (inv, { keepalive } = {}) => api(`/api/invoices/${inv.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (inv) => setInvoices(prev => prev.filter(i => i.id !== inv.id)),
    describe: (inv) => `Invoice ${inv.number} deleted.`,
    failMessage: 'That invoice was not deleted.',
  });
  function deleteInvoice(id) {
    const inv = invoices.find(i => i.id === id);
    if (!inv) return;
    setViewInvoice(null);
    del.remove(inv);
  }

  // What is on the page: everything loaded, less what is waiting behind an Undo.
  const visible = useMemo(() => invoices.filter(i => !del.isHidden(i.id)), [invoices, del]);

  // Filtered list
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visible.filter(inv => {
      const matchSearch = !q
        || inv.number?.toLowerCase().includes(q)
        || inv.contactName?.toLowerCase().includes(q)
        || inv.contactCompany?.toLowerCase().includes(q);
      const matchTab = inTab(inv, activeTab);   // Block H-5: the row's own word
      const matchProject = !projectFilter
        ? true
        : projectFilter === "none"
          ? !inv.project_id
          : String(inv.project_id) === projectFilter;
      return matchSearch && matchTab && matchProject;
    });
  }, [visible, search, activeTab, projectFilter]);

  // The business's own currency comes first in a mixed total (falls back to GBP).
  const baseCurrency = currentUser?.baseCurrency || currentUser?.currencyDisplay || "GBP";

  // Decision 71: "invoiced" excludes drafts and cancelled invoices (as Financial overview).
  const counts = (inv) => inv.status !== "draft" && inv.status !== "cancelled";
  const totalOf = (inv) => calcTotals(inv.lineItems, inv.currency).total;

  // Stats, per currency (decision 72: no conversion; currencies are never added together).
  const stats = useMemo(() => {
    const issued = visible.filter(counts);
    const paidList = visible.filter(i => isPaidStatus(i.status));
    const pendingList = visible.filter(i => invoiceDisplayStatus(i) === "pending");
    const overdueList = visible.filter(i => invoiceDisplayStatus(i) === "overdue");
    const refundedList = visible.filter(i => Number(i.refundedAmount || 0) > 0);
    return {
      issuedCount: issued.length,
      total: moneyByCurrency(issued, totalOf, baseCurrency),
      // Block H-5: what was kept, net of refunds; and what went back, for the tile's second line.
      paid: moneyByCurrency(paidList, (i) => keptTotal(i, totalOf(i)), baseCurrency),
      refunded: refundedList.length ? moneyByCurrency(refundedList, (i) => Number(i.refundedAmount || 0), baseCurrency) : null,
      pending: moneyByCurrency(pendingList, totalOf, baseCurrency),   // Block H-5: matched 'pending', which nothing writes
      // Decision 44 (utils/invoiceOverdue): sent or overdue AND past due, as on Financial overview.
      overdue: moneyByCurrency(overdueList, totalOf, baseCurrency),
      overdueCount: overdueList.length,
      draftCount: visible.filter(i => i.status === "draft").length,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, baseCurrency]);

  // Income by project, per currency, on the same rule as the tiles.
  const byProject = useMemo(() => {
    if (allProjects.length === 0) return [];
    const map = {};
    visible.filter(counts).forEach(inv => {
      const pid = inv.project_id;
      if (!pid) return;
      (map[pid] = map[pid] || []).push(inv);
    });
    return allProjects
      .filter(p => map[p.id])
      .map(p => {
        const list = map[p.id];
        return {
          id: p.id, name: p.name, count: list.length,
          invoiced: moneyByCurrency(list, totalOf, baseCurrency),
          received: moneyByCurrency(list, (i) => keptTotal(i, totalOf(i)), baseCurrency),   // Block H-5: paid, less refunds
          outstanding: moneyByCurrency(list, (i) => totalOf(i) - keptTotal(i, totalOf(i)), baseCurrency),
        };
      })
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, allProjects, baseCurrency]);

  const headerCount = countForTab(visible, "All");
  const newInvoice = () => { setEditTarget(null); setShowForm(true); };
  const tabs = TABS.map(tab => ({ id: tab, label: tab, color: TAB_COLORS[tab], count: countForTab(visible, tab) }));   // Block H-5: the same rule as the list it opens

  const dueMeta = (inv, shown) => {
    if (shown === "draft") return "Not sent";
    if (shown === "cancelled") return "Voided";
    if (isPaidStatus(inv.status)) return inv.paidAt ? `Paid ${shortDate(inv.paidAt)}` : "Paid";
    if (shown === "overdue") return `Was due ${shortDate(inv.dueDate)}`;
    return inv.dueDate ? `Due ${shortDate(inv.dueDate)}` : "No due date";
  };

  return (
    <div className="invc-invoices" data-section="invoices">
      <SectionHead
        title="Invoices"
        subline={(
          <>
            {/* Block H-5: the same number as the All tab (it skipped drafts while All counted them: "6" vs "All (7)"). */}
            <span data-testid="invoices-header-count">{headerCount} invoice{headerCount !== 1 ? "s" : ""}</span>
            {stats.draftCount > 0 && (
              <Chip onClick={() => setActiveTab("Drafts")} selected={activeTab === "Drafts"} aria-label={`View drafts (${stats.draftCount})`}>
                {plural(stats.draftCount, "draft")}
              </Chip>
            )}
            {allowance && (
              <span data-testid="invoices-send-allowance" className={allowance.used >= allowance.limit ? "invc-allowance invc-allowance--full" : "invc-allowance"}>
                {allowance.used} of {allowance.limit} sent this month
              </span>
            )}
          </>
        )}
        action={<HeaderButton primary onClick={newInvoice} data-tour-id="invoicing-new-invoice">+ New invoice</HeaderButton>}
      />

      {/* A FAILED LOAD MUST NOT READ AS "NOTHING TO CHASE" (Carla, 2026-10-03).
          Every figure here is computed from the invoice list, so when the load
          failed the totals are zeros and the Overdue card used to say "All clear".
          She would stop chasing money because the app told her there was none to
          chase. The cards AND the list are replaced by the honest statement (Block K: the list
          under it still said "no invoices"). */}
      {invoicesLoadFailed ? (
        <div data-testid="invoices-load-failed">
          <ErrorState title="Your invoices could not be loaded"
            message={'These figures are not available, so nothing here means "no invoices" or "nothing overdue". Aidey simply does not know right now.'}
            onRetry={refreshInvoices} />
        </div>
      ) : loading ? (
        <Skeleton lines={5} label="Loading invoices" />
      ) : (
        <>
          <section className="invc-kpis" aria-label="Invoice totals">
            <StatTile card color="pastel-periwinkle" label="Invoiced" value={stats.total} hint={`${stats.issuedCount} issued`} />
            <StatTile card color="pastel-mint" label="Paid" value={stats.paid}
              hint={stats.refunded ? `${countForTab(visible, "Paid")} paid · ${stats.refunded} refunded` : `${countForTab(visible, "Paid")} paid`} />
            <StatTile card color="pastel-blue" label="Due" value={stats.pending} hint={plural(countForTab(visible, "Due"), "invoice")} />
            <StatTile card color="pastel-coral" label="Overdue" value={stats.overdue} hint={stats.overdueCount > 0 ? `${stats.overdueCount} overdue` : "All clear"} />
          </section>

          {/* Income by project */}
          {byProject.length > 0 && (
            <section className="invc-table-wrap" aria-label="Income by project">
              <table className="invc-table invc-table--projects">
                <thead>
                  <tr><th>Project</th><th className="is-num">Invoiced</th><th className="is-num">Received</th><th className="is-num">Outstanding</th></tr>
                </thead>
                <tbody>
                  {byProject.map(p => (
                    <tr key={p.id}>
                      <td data-label="Project">
                        {onOpenProjectFinancials
                          ? <button type="button" className="invc-link" onClick={() => onOpenProjectFinancials(p.id)} title="View project financials">{p.name}</button>
                          : <strong>{p.name}</strong>}
                        <span className="invc-small"> ({plural(p.count, "invoice")})</span>
                      </td>
                      <td data-label="Invoiced" className="is-num">{p.invoiced}</td>
                      <td data-label="Received" className="is-num">{p.received}</td>
                      <td data-label="Outstanding" className="is-num">{p.outstanding}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {(() => {
            const statusTabs = <TabGroup tabs={tabs} value={activeTab} onChange={setActiveTab} label="Show invoices" idPrefix="invoices" />;
            const searchBox = (
              <SearchInput label="Search invoices" placeholder={t('invoices.search_placeholder', 'Search by invoice number, contact or company…')}
                value={search} onChange={e => setSearch(e.target.value)} />
            );
            const projectSelect = allProjects.length > 0 && (
              <Select aria-label="Filter by project" value={projectFilter} onChange={e => setProjectFilter(e.target.value)} className="invc-project-filter">
                <option value="">All projects</option>
                <option value="none">Unattached</option>
                {allProjects.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
              </Select>
            );
            // Fresh-eyes item 21: on a phone, the search first and the status and project behind Filter.
            if (phone) {
              const projectName = projectFilter === "none" ? "Unattached" : allProjects.find(p => String(p.id) === projectFilter)?.name;
              const active = [activeTab !== "All" ? activeTab : null, projectFilter ? projectName : null].filter(Boolean);
              return (
                <PhoneListFilters search={searchBox} active={active} title="Filter invoices" done="Show invoices">
                  {statusTabs}
                  {projectSelect}
                </PhoneListFilters>
              );
            }
            return (
              <div className="invc-bar">
                {statusTabs}
                <div className="invc-bar__filters">
                  {searchBox}
                  {projectSelect}
                </div>
              </div>
            );
          })()}

          {visible.length === 0 ? (
            <EmptyState icon="🧾" title="No invoices yet" action={<Button variant="create" onClick={newInvoice}>+ New invoice</Button>}>
              Bill a client in a minute. They pay by card from the link, or by bank transfer.
            </EmptyState>
          ) : filtered.length === 0 ? (
            <p className="invc-muted">{search.trim() ? `No invoices match “${search.trim()}”.` : "No invoices here yet."}</p>
          ) : (
            <div className="invc-grid">
              {filtered.map(inv => {
                const shown = invoiceDisplayStatus(inv);
                const st = STATUS[shown] || STATUS.pending;   // Block H-5: never the raw status
                const total = calcTotals(inv.lineItems, inv.currency).total;
                const mine = isMine(inv);
                const reminders = inv.reminders?.filter(r => r.status === "pending").length || 0;
                const menu = [
                  // IT SAID "Download PDF" AND OPENED A PRINT DIALOGUE. Now it downloads the same
                  // document the client gets. A failure toasts, because the list has nowhere to put a
                  // message and silence would be the worse answer.
                  { label: 'Download PDF', onSelect: async () => { const msg = await downloadInvoicePdf(inv); if (msg) showErrorToast(msg); } },
                  ...(canChange(inv) && !(invoiceGated && inv.approvalStatus === "pending") ? [{ label: 'Edit', onSelect: () => { setEditTarget(inv); setShowForm(true); } }] : []),
                  // UI-24 item 19, decision 2: a sent invoice is cancelled, never deleted.
                  ...(mine && canDeleteInvoice(inv) ? [{ label: 'Delete', danger: true, onSelect: () => deleteInvoice(inv.id) }] : []),
                  ...(canChange(inv) && !canDeleteInvoice(inv) && !isPaidStatus(inv.status) && inv.status !== "cancelled" ? [{ label: 'Cancel invoice', danger: true, onSelect: () => voidInvoice(inv.id) }] : []),
                ];
                return (
                  <Card key={inv.id} data-invoice-row color={st.color} label={st.label} meta={dueMeta(inv, shown)} title={inv.number}
                    footer={(
                      <>
                        {canChange(inv) && !isPaidStatus(inv.status) && inv.status !== "draft" && (
                          <Button variant="confirm" size="sm" onClick={() => markPaid(inv.id)}>Mark paid</Button>
                        )}
                        <Button variant="secondary" size="sm" onClick={() => setViewInvoice(inv)} aria-label={`Open ${inv.number}`}>Open</Button>
                        <OverflowMenu label={`More for ${inv.number}`} items={menu} />
                      </>
                    )}>
                    <span className="invc-card__client invc-wrap">{inv.contactName}{inv.contactCompany ? ` · ${inv.contactCompany}` : ""}</span>
                    {(inv.approvalStatus === "pending" || inv.approvalStatus === "rejected" || !mine) && (
                      <div className="invc-pills">
                        {inv.approvalStatus === "pending" && <StatusPill tone="pending">Awaiting approval</StatusPill>}
                        {inv.approvalStatus === "rejected" && <StatusPill tone="warning">Rejected</StatusPill>}
                        {!mine && <span className="invc-pill">A colleague's</span>}
                      </div>
                    )}
                    {inv.project_id && (
                      onOpenProjectFinancials
                        ? <button type="button" className="invc-link invc-small" onClick={() => onOpenProjectFinancials(inv.project_id)} title="View project financials">{projectsLookup[inv.project_id] || "Project"}</button>
                        : <span className="invc-small">{projectsLookup[inv.project_id] || "Project"}</span>
                    )}
                    <div className="invc-pills invc-card__foot">
                      <span className="invc-pill invc-pill--money">{fmtMoney(total, inv.currency)}</span>
                      {inv.recurring && <span className="invc-pill">Recurring</span>}
                      {inv.linkedTaskId && <span className="invc-pill">Chase task</span>}
                      {reminders > 0 && <span className="invc-pill">{plural(reminders, "reminder")}</span>}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showForm && (
        <InvoiceFormModal
          invoice={editTarget}
          onSave={saveInvoice}
          baseCurrency={baseCurrency}
          onClose={() => { setShowForm(false); setEditTarget(null); setPreselectedProjectId(null); setPreselectedTimeEntries(null); setPreselectedContactId(null); }}
          preselectedProjectId={preselectedProjectId}
          preselectedContactId={preselectedContactId}
          preselectedTimeEntries={preselectedTimeEntries}
        />
      )}

      {viewInvoice && (
        <InvoiceDetailModal
          invoice={viewInvoice}
          projects={allProjects}
          paymentDetails={paymentDetails}
          bookingUsername={bookingUsername}
          gated={invoiceGated}
          canApprove={canApproveInvoices}
          readOnly={!isMine(viewInvoice)}
          canChange={canChange(viewInvoice)}
          xeroAllowed={featureAllowed(currentUser, 'xero')}
          onSubmitForApproval={submitInvoiceForApproval}
          onApprove={approveInvoice}
          onReject={(id) => setRejecting(invoices.find(i => i.id === id) || viewInvoice)}
          onClose={() => setViewInvoice(null)}
          onEdit={inv => { setEditTarget(inv); setShowForm(true); setViewInvoice(null); }}
          onMarkPaid={markPaid}
          onSent={(id, status) => {
            // Only the status changes on a send; everything else stays as loaded.
            // overdue: undefined, so the row reads the date until the next load brings the server's answer.
            setInvoices(prev => prev.map(i => i.id === id ? { ...i, status, overdue: undefined } : i));
            setViewInvoice(v => (v && v.id === id ? { ...v, status, overdue: undefined } : v));
          }}
          onRefunded={(inv) => {
            // Block H-5: the server's whole invoice (status, refunded amount, refunds) replaces the row.
            setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, ...inv } : i));
            setViewInvoice(v => (v && v.id === inv.id ? { ...v, ...inv } : v));
          }}
          onVoid={voidInvoice}
          onUnvoid={unvoidInvoice}
          onDelete={deleteInvoice}
          onUpdateReminders={updateReminders}
          onUpdateProject={updateProject}
        />
      )}

      {rejecting && <RejectModal invoice={rejecting} onClose={() => setRejecting(null)} onReject={rejectInvoice} />}

      {/* Void, Restore, and Mark paid on a cancelled invoice: the server's refusal shows in the dialog. */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          try { await confirm.run(); }
          catch (err) { throw new Error(messageFromError(err, 'That did not work. Please try again.')); }
        }}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        destructive={!!confirm?.destructive}
      >
        {confirm?.option === 'restoreEmail' && (
          <Checkbox checked={restoreEmail} onChange={(v) => { restoreEmailRef.current = !!v; setRestoreEmail(!!v); }} label="Email the client again" />
        )}
      </ConfirmDialog>
    </div>
  );
}

export default InvoicingDashboard;
// Exported for tests: the modal the owner uses to send an invoice (AF-005).
export { InvoiceDetailModal, InvoiceFormModal };   // the form: InvoicePreselect.test.js
